import React, { useState, useEffect, useCallback } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  AdminPanelSettingsOutlined,
  BuildOutlined,
  EditOutlined,
  HubOutlined,
  PersonOutlineRounded,
  TuneOutlined,
  WarningAmberRounded,
  WorkspacesOutlined,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import color from "../../Constants/colors";
import Loading from "../Common/Loading";
import EditUserModal from "./EditUserModal";
import AlanQuota from "./AlanQuota";
import MicrosoftConnection from "./MicrosoftConnection";
import PushNotificationSettings from "./PushNotificationSettings";
import {
  ProfileCard,
  SettingRow,
  StatusPill,
  profileButtonSx,
} from "./ProfileSection";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useUser, useAlanQuota } from "../../Query/useUsers";
import { useCurrentWorkspace } from "../../Query/useWorkspace";
import {
  hasPermission,
  hasRole,
  userRoleToString,
} from "../../Helpers/authHelpers";
import { Features, UserRole } from "../../Enums/companyStatus";
import { toggleMaintenanceMode } from "../../Api/auth";
import { useQueryClient } from "@tanstack/react-query";

const ProfileInformation: React.FC = () => {
  const { t } = useTranslation("profile");
  const { colors: workspaceColors } = useWorkspaceColors();
  const { data: userData, isLoading: userLoading } = useUser();
  const { data: workspaceData, isLoading: workspaceLoading } =
    useCurrentWorkspace();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const queryClient = useQueryClient();
  const isAdmin = hasRole(UserRole.ADMIN);
  const { data: alanQuotaData } = useAlanQuota(isAdmin);
  // The Microsoft connection is only used by the SharePoint sync of entities.
  const canConnectMicrosoft =
    isAdmin || hasPermission(Features.ENTITY_CREATION);
  const isMaintenanceOn = userData?.maintenance_mode === true;

  useEffect(() => {
    if (!confirmDialogOpen) {
      setCountdown(5);
      return;
    }
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [confirmDialogOpen, countdown]);

  const handleConfirmMaintenance = async () => {
    setConfirmDialogOpen(false);
    setMaintenanceLoading(true);
    try {
      await toggleMaintenanceMode(true);
      queryClient.invalidateQueries({ queryKey: ["user"] });
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const handleDisableMaintenance = async () => {
    setMaintenanceLoading(true);
    try {
      await toggleMaintenanceMode(false);
      queryClient.invalidateQueries({ queryKey: ["user"] });
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const handleToggleMaintenance = useCallback(() => {
    if (isMaintenanceOn) {
      handleDisableMaintenance();
    } else {
      setConfirmDialogOpen(true);
    }
  }, [isMaintenanceOn]);

  if (!userData || userLoading || workspaceLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspaceColors.accent}
        color2={workspaceColors.primary_dark}
      />
    );
  }

  const fullName = [userData.name, userData.surname].filter(Boolean).join(" ");
  const initials =
    [userData.name, userData.surname]
      .map((part) => part?.trim().charAt(0) ?? "")
      .join("")
      .toUpperCase() || userData.email.charAt(0).toUpperCase();
  const workspaceName = userData.workspace?.name;
  const workspaceIcon = workspaceData?.workspace?.icon_image
    ? `data:image/png;base64,${workspaceData.workspace.icon_image}`
    : null;
  // Evaluation and entity limits live on the home dashboard; the profile only
  // keeps the admin's Alan quota next to the settings.
  const showUsage = isAdmin && Boolean(alanQuotaData);

  const header = (
    <ProfileCard sx={{ p: { xs: 2.5, sm: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 2, sm: 3 },
        }}
      >
        <Avatar
          sx={{
            width: { xs: 64, sm: 80 },
            height: { xs: 64, sm: 80 },
            fontSize: { xs: 24, sm: 30 },
            fontWeight: 700,
            backgroundColor: color.primary_dark,
            color: color.white,
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: 21, sm: 24 },
              fontWeight: 700,
              lineHeight: 1.25,
              color: color.primary_dark,
              wordBreak: "break-word",
            }}
          >
            {fullName || userData.email}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 14,
              color: color.gray500,
              wordBreak: "break-all",
            }}
          >
            {userData.email}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.5 }}>
            <StatusPill tone="neutral">
              {isAdmin ? (
                <AdminPanelSettingsOutlined />
              ) : (
                <PersonOutlineRounded />
              )}
              {t(userRoleToString(userData.role))}
            </StatusPill>
            {workspaceName && (
              <StatusPill tone="neutral">
                {workspaceIcon ? (
                  <Box
                    component="img"
                    src={workspaceIcon}
                    alt=""
                    sx={{ width: 15, height: 15, objectFit: "contain" }}
                  />
                ) : (
                  <WorkspacesOutlined />
                )}
                {workspaceName}
              </StatusPill>
            )}
            {userData.is_sso && (
              <StatusPill tone="neutral">
                <Box
                  component="img"
                  src="/microsoft_logo.png"
                  alt=""
                  sx={{ width: 13, height: 13 }}
                />
                {t("profile.ssoLabel")}
              </StatusPill>
            )}
          </Box>
        </Box>
        <Button
          onClick={() => setEditModalOpen(true)}
          startIcon={<EditOutlined />}
          sx={profileButtonSx("outlined")}
        >
          {t("profile.editButton")}
        </Button>
      </Box>
    </ProfileCard>
  );

  const usageColumn = showUsage && (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {isAdmin && alanQuotaData && <AlanQuota alanQuotaData={alanQuotaData} />}
    </Box>
  );

  const settingsColumn = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <ProfileCard title={t("profile.connectionsTitle")} icon={<HubOutlined />}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {canConnectMicrosoft && <MicrosoftConnection />}
          <PushNotificationSettings />
        </Box>
      </ProfileCard>

      {isAdmin && (
        <ProfileCard
          title={t("profile.administrationTitle")}
          icon={<TuneOutlined />}
        >
          <SettingRow
            tone={isMaintenanceOn ? "warning" : "default"}
            icon={<BuildOutlined />}
            title={t("profile.maintenanceTitle")}
            description={
              isMaintenanceOn
                ? t("profile.maintenanceStatusOn")
                : t("profile.maintenanceStatusOff")
            }
            action={
              <Button
                onClick={handleToggleMaintenance}
                disabled={maintenanceLoading}
                startIcon={
                  maintenanceLoading ? (
                    <CircularProgress size={14} sx={{ color: "inherit" }} />
                  ) : undefined
                }
                sx={
                  isMaintenanceOn
                    ? profileButtonSx("primary")
                    : {
                        ...profileButtonSx("outlined"),
                        color: color.orange600,
                        borderColor: color.orange200,
                        "&:hover": {
                          backgroundColor: color.orange50,
                          borderColor: color.orange500,
                        },
                      }
                }
              >
                {isMaintenanceOn
                  ? t("profile.maintenanceDisable")
                  : t("profile.maintenanceEnable")}
              </Button>
            }
          />
        </ProfileCard>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          maxWidth: 1120,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        {header}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: showUsage
              ? { xs: "minmax(0, 1fr)", lg: "minmax(0, 3fr) minmax(0, 2fr)" }
              : "minmax(0, 1fr)",
            gap: 2.5,
            alignItems: "start",
          }}
        >
          {usageColumn}
          {settingsColumn}
        </Box>

        <EditUserModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          userData={{
            name: userData.name,
            surname: userData.surname,
            email: userData.email,
            is_sso: userData.is_sso,
          }}
        />

        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: "16px" } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              pt: 3,
              color: color.primary_dark,
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            <WarningAmberRounded
              sx={{ color: color.orange600, fontSize: 28 }}
            />
            {t("profile.maintenanceConfirmTitle")}
          </DialogTitle>
          <DialogContent>
            <Typography
              sx={{
                mb: 2.5,
                fontSize: 14.5,
                color: color.red700,
                fontWeight: 500,
              }}
            >
              {t("profile.maintenanceConfirmMessage")}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={((5 - countdown) / 5) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: color.gray200,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: color.orange500,
                  borderRadius: 3,
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button
              onClick={() => setConfirmDialogOpen(false)}
              sx={profileButtonSx("outlined")}
            >
              {t("profile.maintenanceConfirmCancel")}
            </Button>
            <Button
              onClick={handleConfirmMaintenance}
              disabled={countdown > 0}
              startIcon={<BuildOutlined />}
              sx={{
                ...profileButtonSx("primary"),
                backgroundColor: color.orange600,
                borderColor: color.orange600,
                "&:hover": { backgroundColor: color.orange600, opacity: 0.9 },
                "&.Mui-disabled": {
                  backgroundColor: color.orange200,
                  borderColor: color.orange200,
                  color: color.white,
                },
              }}
            >
              {countdown > 0
                ? t("profile.maintenanceConfirmWait", { seconds: countdown })
                : t("profile.maintenanceConfirmActivate")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ProfileInformation;
